import { htmlToPlainText } from '~/composables/htmlToPlainText'
import { splitSeparatorSiteSuffix } from '~/composables/siteName'
import { composeTitle } from '~/composables/useSeo'
import { getPrimaryImage } from '~/utils/getPrimaryImage'

// Strip a separator-joined site-name suffix ("Page | 180 Medical" -> "Page") so schema values
// surface a clean page name; prose ("…at 180 Medical") and bare "180 Medical" are kept intact.
function stripSiteNameSuffix(title: string): string {
  return (splitSeparatorSiteSuffix(title) ?? title).trim() || title
}

/**
 * Resolved SEO metadata for a CMS page.
 *
 * Shared shape between the rendered <title>, <meta description>, og:* tags
 * (via useSeo) and the JSON-LD WebPage/Article schema (via useSchemaForContent)
 * so the same values flow into all three SEO surfaces.
 */
export interface ResolvedSeoMetadata {
  title: string
  documentTitle: string
  description: string
  url?: string
  images: { url: string, altText: string }[]
  noIndex: boolean
  noFollow: boolean
}

/**
 * Resolve SEO metadata from an Optimizely content node.
 *
 * Resolution rules (single source of truth — change here, both useSeo and
 * useSchemaForContent pick it up):
 *
 * - title:       product.seoTitle ▸ product.title ▸ pageMetaContent.sharingTitle
 *                ▸ pageMetaContent.navigationTitle ▸ content.name
 * - documentTitle: an authored SEO title (product.seoTitle, sharingTitle) is the
 *                entire <title>; only fallback sources get " - 180 Medical" appended
 * - description: product.seoDescription ▸ pageMetaContent.sharingDescription
 *                ▸ content.pageSummary
 * - image:       product.images[isPrimary] (gallery image, falling back to
 *                images[0]) ▸ pageMetaContent.aprimoSharingImage
 *
 * Product-page fields take priority because for product pages the CMS exposes
 * the page-specific metadata under `content.product.*` rather than the
 * generic `content.pageMetaContent.*` (which is empty on the product template).
 *
 * url is optional — pass currentUrl.href from the page context when invoking
 * from a server-rendered route.
 */
export function resolveSeoMetadata(content: any, url?: string): ResolvedSeoMetadata {
  const pageMeta = content?.pageMetaContent
  const product = content?.product
  const productTitle = htmlToPlainText(product?.title)

  const titleSources: { value?: string, authored?: boolean }[] = [
    { value: product?.seoTitle, authored: true },
    { value: productTitle },
    { value: pageMeta?.sharingTitle?.value, authored: true },
    { value: pageMeta?.navigationTitle?.value },
    { value: content?.name },
  ]
  const titleSource = titleSources.find(source => source.value)
  const rawTitle = titleSource?.value || ''
  // CMS stores SEO titles with the "| 180 Medical" site suffix baked in. Strip
  // it so JSON-LD WebPage.name / Article.headline / breadcrumb names match the
  // displayed page heading, not the browser tab title.
  const title = stripSiteNameSuffix(rawTitle)
  const documentTitle = (titleSource?.authored && rawTitle.trim()) || composeTitle(title)

  const description
    = product?.seoDescription
      || pageMeta?.sharingDescription?.value
      || content?.pageSummary?.value
      || ''

  const productImage = getPrimaryImage(product?.images)
  const sharingImageUrl = pageMeta?.aprimoSharingImage?.value?.url
  let images: ResolvedSeoMetadata['images'] = []
  if (productImage?.url) {
    images = [{
      url: productImage.url,
      altText: productImage.altText || productTitle || content?.name || '',
    }]
  }
  else if (sharingImageUrl) {
    images = [{
      url: sharingImageUrl,
      altText: pageMeta?.sharingTitle?.value || content?.name || '',
    }]
  }

  return {
    title,
    documentTitle,
    description,
    url,
    images,
    noIndex: pageMeta?.suppressFromExternalSearchEngines?.value === true,
    noFollow: pageMeta?.suppressLinkFollowing?.value === true,
  }
}
